import React, { useRef, Fragment, Suspense, MutableRefObject } from 'react';
import clsx from 'clsx';
import { Dialog, Transition } from '@headlessui/react';
import { CloseIcon as Close } from '@td-client-frontend/icons';
import { ConfirmButtonArray } from '@td-client-frontend/common';

import LoadingFallback from '../LoadingFallback';
import { Button } from '../form-controls/Button';
import ReactTooltip from 'react-tooltip';

const modalSizes = {
  small: 'max-w-lg w-full lg:max-w-xl mx-10 lg:mx-0',
  medium: 'max-w-3xl w-full lg:max-w-3xl mx-10 lg:mx-0',
  large: 'max-w-8xl w-full h-full max-h-9v lg:max-w-6xl mx-10 lg:mx-0',
  default: 'max-w-lg w-full lg:max-w-2xl mx-10 lg:mx-0 max-h-lg',
  auto: 'max-w-lg lg:max-w-2xl mx-10 lg:mx-0 max-h-lg',
  prompt: 'max-w-lg w-full lg:max-w-2xl mx-10 lg:mx-0 max-h-lg'
};

interface CustomFooterActions {
  left?: React.ReactNode;
  right?: React.ReactNode;
}

export interface FooterActions {
  title: string;
  kind?: string;
  onClick?: (e?: any) => void;
  actionCancelButtonLabel?: string;
  autoClose?: boolean;
  isDisabled?: () => boolean;
  hasConfirm?: boolean;
  name?: string;
  confirmProps?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  header: string;
  children: React.ReactNode;
  size?: keyof typeof modalSizes;
  actions?: Array<FooterActions>;
  customFooterActions?: CustomFooterActions;
  contentLabel?: string;
  title?: string;
  dataTestid?: string;
}

export function CustomModal({
  isOpen,
  onRequestClose,
  contentLabel,
  children,
  title
}: {
  isOpen: boolean;
  onRequestClose: () => void;
  contentLabel: string;
  children: React.ReactNode;
  title: string;
}) {
  return (
    <Transition
      show={isOpen}
      enter="transition duration-100 ease-out"
      enterFrom="transform scale-95 opacity-0"
      enterTo="transform scale-100 opacity-100"
      leave="transition duration-75 ease-out"
      leaveFrom="transform scale-100 opacity-100"
      leaveTo="transform scale-95 opacity-0"
    >
      <Dialog onClose={onRequestClose} className="fixed z-50 flex items-center justify-center">
        <div className="flex flex-col w-full h-full overflow-visible bg-white rounded-lg">
          <Dialog.Overlay />

          <Dialog.Title className="m-0 text-3xl font-effra">{title}</Dialog.Title>
        </div>
        {children}
      </Dialog>
    </Transition>
  );
}

function ModalContainer({
  children,
  onClose,
  isOpen,
  initialFocus
}: {
  children: React.ReactNode;
  onClose: () => void;
  isOpen: boolean;
  initialFocus: MutableRefObject<any>;
}) {
  return (
    <Transition show={isOpen} as={Fragment} appear afterEnter={() => ReactTooltip.rebuild()}>
      <Dialog
        onClose={onClose}
        initialFocus={initialFocus}
        className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto font-effra"
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-75"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-75"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black" style={{ opacity: 0.2 }} />
        </Transition.Child>
        <Transition.Child
          as={Fragment}
          enter="transition ease-in-out duration-200 transform"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition ease-in-out duration-75 transform"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {children}
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}

function ModalCloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      onClick={onClose}
      className="cursor-pointer focus:outline-none focus-visible:ring"
      type="button"
      aria-label="Close Modal"
      title="Close Modal"
    >
      <Close className="fill-current text-gray-50 hover:text-black" width={24} height={24} aria-hidden="true" />
    </button>
  );
}

function ModalHeader({
  onClose,
  children,
  icon = null,
  size = 'default'
}: {
  children: string;
  onClose: () => void;
  icon?: React.ReactNode | null;
  size?: keyof typeof modalSizes;
}) {
  const dialogSize = size === 'prompt';
  return (
    <div
      className={clsx(
        'flex justify-between items-center px-6 py-4',
        dialogSize ? 'border-b-0' : 'border-b-1 border-gray-20'
      )}
    >
      {icon}
      <Dialog.Title className="m-0 text-3xl font-medium">{children}</Dialog.Title>
      {!dialogSize && <ModalCloseButton onClose={onClose} />}
    </div>
  );
}

function ModalContent({ children }: { children: React.ReactNode }) {
  return (
    <div data-testid="modal" className="flex flex-grow min-h-12">
      {children}
    </div>
  );
}

export function ModalFooterButtonGroup({
  children,
  customFooterActions = true,
  size = 'default',
  className = ''
}: {
  children: React.ReactNode;
  customFooterActions?: boolean;
  size?: string;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'p-6 sm:px-8 py-4 flex sm:justify-end gap-4 flex-grow-0 rounded-b-lg',
        !customFooterActions || size === 'prompt' ? 'pt-0' : 'bg-gray-10 border-t-1 border-gray-20',
        className
      )}
    >
      {children}
    </div>
  );
}

function ModalFooter({
  actions,
  withoutContent,
  onClose,
  customFooterActions,
  size
}: {
  actions?: FooterActions[];
  withoutContent: boolean;
  onClose: () => void;
  customFooterActions: any;
  size: string;
}) {
  return (
    <ModalFooterButtonGroup customFooterActions={customFooterActions} size={size}>
      {customFooterActions && (
        <div className="flex flex-1 justify-between">
          <div>{customFooterActions.left}</div>

          <div>{customFooterActions.right}</div>
        </div>
      )}

      {!customFooterActions &&
        actions &&
        actions.map(({ autoClose, ...a }) => {
          const onClick = (e?: any) => {
            a.onClick && a.onClick(e);
            // TODO investigate when footer button triggers promise or should not close dialog
            if (autoClose !== false) {
              onClose();
            }
          };

          if (a.hasConfirm) {
            return (
              <ConfirmButtonArray
                key={a.name || a.title}
                id="modal_save_confirm"
                onClick={onClick}
                tooltipOverrides={{
                  place: 'top',
                  arrowColor: '#5b2f91',
                  offset: {},
                  isModalConfirm: true
                }}
                customization={a.confirmProps || null}
              >
                <div className="btn btn-primary">{a.title}</div>
              </ConfirmButtonArray>
            );
          }

          return (
            <Button
              key={a.name || a.title}
              {...a}
              onClick={onClick}
              disabled={a.isDisabled && a.isDisabled()}
              className="w-full sm:ml-3 sm:w-auto sm:text-sm"
            >
              {a.name || a.title}
            </Button>
          );
        })}
    </ModalFooterButtonGroup>
  );
}

export default function Modal({
  isOpen,
  onClose,
  header,
  children,
  size = 'default',
  actions,
  customFooterActions,
  dataTestid
}: ModalProps) {
  const completeButtonRef = useRef(null);
  const withoutContent = !children;
  return (
    <ModalContainer onClose={onClose} isOpen={isOpen} initialFocus={completeButtonRef}>
      <div
        className={clsx('flex flex-col z-50 text-left bg-white rounded-lg shadow-lg sm:align-middle', modalSizes[size])}
        ref={completeButtonRef}
        data-testid={dataTestid}
      >
        <ModalHeader onClose={onClose} size={size}>
          {header}
        </ModalHeader>

        <ModalContent>
          <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
        </ModalContent>

        {/* Button Group */}
        {(actions || customFooterActions) && (
          <ModalFooter
            size={size}
            actions={actions}
            onClose={onClose}
            withoutContent={withoutContent}
            customFooterActions={customFooterActions}
          />
        )}
      </div>
    </ModalContainer>
  );
}
